import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import VideoResourcesHeader from '@/components/video-generator/VideoResourcesHeader';
import Container from '@/components/ui/Container';
import SidebarLayout from '@/components/layout/SidebarLayout';
import VideoFilterSection from '@/components/video-generator/VideoFilterSection';
import VideoLibrary from '@/components/video-generator/VideoLibrary';
import { useVideoFilters } from '@/hooks/video-generator/video-filters';
import { useResourceVideoGeneration } from '@/hooks/video-generator/useVideoGeneration';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import ComingSoonModal from '@/components/ui/ComingSoonModal';

const VideoGenerator = () => {
  const navigate = useNavigate();
  
  // Coming soon modal state
  const [showComingSoon, setShowComingSoon] = useState(true);
  
  // Get video filters state and handlers - now using atomic state management
  const {
    hasAudio,
    setHasAudio,
    styleOptions,
    selectedStyle,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    setSelectedInfluence,
    colorOptions,
    backgroundOptions,
    selectedBackground,
    setSelectedBackground,
    environmentOptions,
    moodOptions,
    handleStyleChange,
    handleInfluenceChange,
    handleColorChange,
    handleBackgroundChange,
    handleEnvironmentChange,
    handleMoodChange,
    videoDescription,
    setVideoDescription,
    debouncedVideoDescription
  } = useVideoFilters();
  
  // Use the custom video generation hook
  const {
    isGenerating,
    selectedVideo,
    setSelectedVideo,
    handleGenerateVideo
  } = useResourceVideoGeneration({
    videoDescription,
    hasAudio,
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    styleOptions,
    influenceOptions,
    colorOptions,
    backgroundOptions,
    environmentOptions,
    moodOptions
  });

  // Stable event handlers - memoized to prevent unnecessary re-renders
  const handleAudioChange = useCallback((audio: boolean) => {
    setHasAudio(audio);
  }, [setHasAudio]);

  const handleVideoDescriptionChange = useCallback((value: string) => {
    setVideoDescription(value);
  }, [setVideoDescription]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Memoized filter IDs with stable references
  const filterIds = useMemo(() => ({
    environmentIds: environmentOptions.filter(o => o.checked).map(o => o.id),
    moodIds: moodOptions.filter(o => o.checked).map(o => o.id),
    colorIds: colorOptions.filter(o => o.checked).map(o => o.id)
  }), [environmentOptions, moodOptions, colorOptions]);

  // Memoized sidebar props with stable references
  const sidebarProps = useMemo(() => ({
    hasAudio,
    onAudioChange: handleAudioChange,
    styleOptions,
    selectedStyle,
    onStyleChange: handleStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    onInfluenceChange: handleInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    onColorChange: handleColorChange,
    backgroundOptions,
    onBackgroundChange: handleBackgroundChange,
    selectedBackground,
    environmentOptions,
    onEnvironmentChange: handleEnvironmentChange,
    moodOptions,
    onMoodChange: handleMoodChange,
    videoDescription,
    onVideoDescriptionChange: handleVideoDescriptionChange,
    isGenerating,
    onGenerate: handleGenerateVideo
  }), [
    hasAudio,
    handleAudioChange,
    styleOptions,
    selectedStyle,
    handleStyleChange,
    setSelectedStyle,
    influenceOptions,
    selectedInfluence,
    handleInfluenceChange,
    setSelectedInfluence,
    colorOptions,
    handleColorChange,
    backgroundOptions,
    handleBackgroundChange,
    selectedBackground,
    environmentOptions,
    handleEnvironmentChange,
    moodOptions,
    handleMoodChange,
    videoDescription,
    handleVideoDescriptionChange,
    isGenerating,
    handleGenerateVideo
  ]);

  // Memoized library props with stable references
  const libraryProps = useMemo(() => ({
    styleId: selectedStyle,
    influenceId: selectedInfluence,
    backgroundId: selectedBackground,
    environmentIds: filterIds.environmentIds,
    moodIds: filterIds.moodIds,
    colorIds: filterIds.colorIds,
    hasAudio,
    description: videoDescription,
    debouncedDescription: debouncedVideoDescription,
    selectedVideo,
    onVideoSelect: setSelectedVideo
  }), [
    selectedStyle,
    selectedInfluence,
    selectedBackground,
    filterIds.environmentIds,
    filterIds.moodIds,
    filterIds.colorIds,
    hasAudio,
    videoDescription,
    debouncedVideoDescription,
    selectedVideo,
    setSelectedVideo
  ]);

  // Memoized sidebar content with stable reference
  const sidebarContent = useMemo(() => (
    <VideoFilterSection {...sidebarProps} />
  ), [sidebarProps]);

  return (
    <>
      <MobilePageWrapper>
        <Container>
          <VideoResourcesHeader />
          <div className="space-y-3 md:space-y-6">
            <SidebarLayout sidebar={sidebarContent}>
              <VideoLibrary {...libraryProps} />
            </SidebarLayout>
          </div>
        </Container>
      </MobilePageWrapper>
      
      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoon}
        onBack={handleGoBack}
        featureName="Video Generator"
        description="Our AI video generation feature is currently in development. Soon you'll be able to create stunning videos with just a text prompt!"
      />
    </>
  );
};

export default VideoGenerator; 